import React, { useEffect, useRef, useState } from 'react';

import { Checkbox, TextField } from '@mui/material';
import { GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid';

/**
 * Shared DataGrid edit cell components for the Accommodation module.
 *
 * There are two patterns for inline editing in DataGrid:
 *
 * 1. **Always-visible pattern** - Edit controls are always shown in the cell (no click required).
 *    Use `InlineNumberEditCell` and `InlineCheckboxEditCell` in `renderCell` with editable: false (default).
 *    These components accept an `onChange` callback for direct state updates.
 *
 * 2. **Click-to-edit pattern** - User must click the cell to enter edit mode.
 *    Use `EditableTextField`, `NumberEditCell`, `CheckboxEditCell`, `AliasesEditCell` in `renderEditCell` with editable: true.
 *    These components use `useGridApiContext` to update the DataGrid's internal state.
 */

// =============================================================================
// Always-visible pattern components (use in renderCell with editable: false)
// =============================================================================

interface InlineNumberEditCellProps {
  value: number | undefined | null;
  onChange: (value: number | undefined) => void;
}

/**
 * Always-visible number input for inline editing without requiring a click to enter edit mode.
 * Use in `renderCell` with the column's `editable` set to `false` (default).
 */
export function InlineNumberEditCell({ value, onChange }: InlineNumberEditCellProps) {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const numValue = event.target.value === '' ? undefined : Number(event.target.value);
    onChange(numValue);
  };

  // Stop propagation to prevent DataGrid from capturing keyboard events
  const handleKeyDown = (event: React.KeyboardEvent) => {
    event.stopPropagation();
  };

  return (
    <TextField
      size="small"
      type="number"
      value={value ?? ''}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      fullWidth
      variant="outlined"
    />
  );
}

interface InlineCheckboxEditCellProps {
  value: boolean | undefined | null;
  onChange: (value: boolean) => void;
}

/**
 * Always-visible checkbox for inline editing without requiring a click to enter edit mode.
 * Use in `renderCell` with the column's `editable` set to `false` (default).
 */
export function InlineCheckboxEditCell({ value, onChange }: InlineCheckboxEditCellProps) {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.checked);
  };

  return <Checkbox checked={Boolean(value)} onChange={handleChange} />;
}

interface InlineTextFieldEditCellProps {
  value: string | undefined | null;
  onChange: (value: string) => void;
  placeholder?: string;
}

const DEBOUNCE_MS = 300;

/**
 * Always-visible text input for inline editing without requiring a click to enter edit mode.
 * Use in `renderCell` with the column's `editable` set to `false` (default).
 *
 * Uses local state for smooth typing and debounces onChange to avoid performance issues
 * from re-rendering the entire grid on every keystroke.
 */
export function InlineTextFieldEditCell({ value, onChange, placeholder }: InlineTextFieldEditCellProps) {
  const [localValue, setLocalValue] = useState(value ?? '');
  const [isFocused, setIsFocused] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local state when the external value changes, but only when not focused
  // to avoid interrupting the user while typing
  useEffect(() => {
    if (!isFocused) {
      setLocalValue(value ?? '');
    }
  }, [value, isFocused]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setLocalValue(newValue);

    // Clear any existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce the onChange call
    debounceTimerRef.current = setTimeout(() => {
      onChange(newValue);
    }, DEBOUNCE_MS);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Clear any pending debounce and trigger onChange immediately on blur
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (localValue !== (value ?? '')) {
      onChange(localValue);
    }
  };

  // Stop propagation to prevent DataGrid from capturing keyboard events
  // (space for selection, arrow keys for navigation)
  const handleKeyDown = (event: React.KeyboardEvent) => {
    event.stopPropagation();
  };

  return (
    <TextField
      size="small"
      value={localValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      fullWidth
      variant="outlined"
      placeholder={placeholder}
    />
  );
}

// =============================================================================
// Click-to-edit pattern components (use in renderEditCell with editable: true)
// =============================================================================

/**
 * Text field for click-to-edit mode. Uses DataGrid's internal state via apiRef.
 * Use in `renderEditCell` with the column's `editable` set to `true`.
 */
export function EditableTextField(props: GridRenderEditCellParams) {
  const { id, field, value } = props;
  const apiRef = useGridApiContext();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    apiRef.current.setEditCellValue({ id, field, value: event.target.value });
  };

  return <TextField size="small" value={value ?? ''} onChange={handleChange} fullWidth variant="outlined" />;
}

/**
 * Number field for click-to-edit mode. Uses DataGrid's internal state via apiRef.
 * Use in `renderEditCell` with the column's `editable` set to `true`.
 */
export function NumberEditCell(props: GridRenderEditCellParams) {
  const { id, field, value } = props;
  const apiRef = useGridApiContext();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const numValue = event.target.value === '' ? undefined : Number(event.target.value);
    apiRef.current.setEditCellValue({ id, field, value: numValue });
  };

  return (
    <TextField size="small" type="number" value={value ?? ''} onChange={handleChange} fullWidth variant="outlined" />
  );
}

/**
 * Checkbox for click-to-edit mode. Uses DataGrid's internal state via apiRef.
 * Use in `renderEditCell` with the column's `editable` set to `true`.
 */
export function CheckboxEditCell(props: GridRenderEditCellParams) {
  const { id, field, value } = props;
  const apiRef = useGridApiContext();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    apiRef.current.setEditCellValue({ id, field, value: event.target.checked });
  };

  return <Checkbox checked={Boolean(value)} onChange={handleChange} />;
}

/**
 * Comma-separated aliases field for click-to-edit mode. Uses DataGrid's internal state via apiRef.
 * Use in `renderEditCell` with the column's `editable` set to `true`.
 */
export function AliasesEditCell(props: GridRenderEditCellParams) {
  const { id, field, value } = props;
  const apiRef = useGridApiContext();
  const aliasesValue = Array.isArray(value) ? value.join(', ') : '';

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    apiRef.current.setEditCellValue({ id, field, value: newValue });
  };

  return (
    <TextField
      size="small"
      value={aliasesValue}
      onChange={handleChange}
      fullWidth
      variant="outlined"
      placeholder="Comma-separated aliases"
    />
  );
}
